import React, { useState, useEffect } from 'react';
import { WatchlistService } from '../../services/WatchlistService';
import type { WatchlistWithQuotes } from '../../services/WatchlistService';
import { SearchBar } from '../ui/SearchBar';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { ScrollableTabBar } from '../ui/ScrollableTabBar';
import { WatchlistTable } from './WatchlistTable';
import { useToast } from '../../hooks/useToast';
import { PlusCircle, X, Eye } from 'lucide-react';
import type { SearchResult } from '../../types/market';

interface WatchlistManagerEnhancedProps {
  userId: string;
  className?: string;
}

/* ── Shared inline-input style ── */
const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: '0.5rem 0.75rem',
  background: '#1a2235',
  border: '1px solid #1e2d45',
  borderRadius: 8,
  color: '#f1f5f9',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  outline: 'none',
  minWidth: 0,
  transition: 'border-color 0.18s, box-shadow 0.18s',
};

export const WatchlistManagerEnhanced: React.FC<WatchlistManagerEnhancedProps> = ({
  userId,
  className = '',
}) => {
  const [watchlists, setWatchlists] = useState<WatchlistWithQuotes[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [nameInputFocused, setNameInputFocused] = useState(false);
  const { addToast } = useToast();

  useEffect(() => { loadWatchlists(); }, [userId]);
  useEffect(() => {
    const unsub = WatchlistService.subscribeToWatchlistChanges(userId, loadWatchlists);
    return unsub;
  }, [userId]);

  const loadWatchlists = async () => {
    try {
      setIsLoading(true);
      const data = await WatchlistService.getUserWatchlists(userId, true);
      setWatchlists(data);
      if (data.length > 0 && !activeId) setActiveId(data[0].id);
    } catch {
      addToast('Failed to load watchlists', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) { addToast('Enter a watchlist name', 'warning'); return; }
    try {
      setIsCreating(true);
      const created = await WatchlistService.createWatchlist({ user_id: userId, name: newName.trim(), stocks: [] });
      setWatchlists(p => [{ ...created, quotes: [] }, ...p]);
      setNewName('');
      setShowCreate(false);
      setActiveId(created.id);
      addToast(`"${created.name}" created`, 'success');
    } catch {
      addToast('Failed to create watchlist', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    try {
      await WatchlistService.deleteWatchlist(id);
      setWatchlists(p => p.filter(w => w.id !== id));
      if (activeId === id) {
        const rest = watchlists.filter(w => w.id !== id);
        setActiveId(rest.length > 0 ? rest[0].id : null);
      }
      addToast(`"${name}" deleted`, 'success');
    } catch {
      addToast('Failed to delete watchlist', 'error');
    }
  };

  const handleAddStock = async (result: SearchResult) => {
    if (!activeId) { addToast('Select a watchlist first', 'warning'); return; }
    try {
      await WatchlistService.addStockToWatchlist(activeId, result.symbol, result.name);
      await loadWatchlists();
      addToast(`${result.symbol} added`, 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to add stock', 'error');
    }
  };

  const handleRemoveStock = async (ticker: string) => {
    if (!activeId) return;
    try {
      await WatchlistService.removeStockFromWatchlist(activeId, ticker);
      await loadWatchlists();
      addToast(`${ticker} removed`, 'success');
    } catch {
      addToast('Failed to remove stock', 'error');
    }
  };

  const active = watchlists.find(w => w.id === activeId);
  const upCount   = active?.quotes.filter(q => q.change > 0).length ?? 0;
  const downCount = active?.quotes.filter(q => q.change < 0).length ?? 0;

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2.5rem' }}>
        <LoadingSpinner size="md" />
      </div>
    );
  }

  /* ── Empty state ── */
  if (watchlists.length === 0 && !showCreate) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%',
          background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1.125rem',
        }}>
          <Eye size={22} color="#3b82f6" strokeWidth={1.75} />
        </div>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#cbd5e1', marginBottom: '0.375rem' }}>
          No watchlists yet
        </h3>
        <p style={{ fontSize: '0.8125rem', color: '#334155', marginBottom: '1.25rem' }}>
          Create a watchlist to track stocks you're watching
        </p>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#3b82f6', color: '#fff',
            padding: '0.5625rem 1.125rem', borderRadius: 9,
            fontSize: '0.8125rem', fontWeight: 600, border: 'none', cursor: 'pointer',
            transition: 'background 0.18s, box-shadow 0.18s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(59,130,246,0.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#3b82f6'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <PlusCircle size={14} strokeWidth={2} /> Create Watchlist
        </button>
      </div>
    );
  }

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap' }}>
        {/* Tab bar */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ScrollableTabBar
            tabs={watchlists.map(w => ({ id: w.id, label: w.name, count: w.stocks.length, isActive: w.id === activeId }))}
            activeTabId={activeId}
            onTabClick={setActiveId}
            onDeleteTab={handleDelete}
          />
        </div>

        {/* Right actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap' }}>
          {/* Search — add stock */}
          {active && (
            <div className="hidden sm:block" style={{ width: 220 }}>
              <SearchBar
                onSelect={handleAddStock}
                placeholder="Add stock…"
                className="w-full"
              />
            </div>
          )}
          {/* Create new */}
          <button
            onClick={() => setShowCreate(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '0.4375rem 0.875rem', borderRadius: 8,
              background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)',
              color: '#60a5fa', fontSize: '0.8125rem', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.18s', whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.12)'; }}
          >
            <PlusCircle size={13} strokeWidth={2} /> New Watchlist
          </button>
        </div>
      </div>

      {/* Mobile search */}
      {active && (
        <div className="sm:hidden">
          <SearchBar onSelect={handleAddStock} placeholder="Add stock to watchlist…" className="w-full" />
        </div>
      )}

      {/* ── Create form ── */}
      {showCreate && (
        <div style={{
          background: '#1a2235', border: '1px solid #1e2d45',
          borderRadius: 10, padding: '0.875rem',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          animation: 'slideUp 0.18s ease-out',
        }}>
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Watchlist name…"
            autoFocus
            style={{
              ...inputStyle,
              borderColor: nameInputFocused ? '#3b82f6' : '#1e2d45',
              boxShadow: nameInputFocused ? '0 0 0 2px rgba(59,130,246,0.15)' : 'none',
            }}
            onFocus={() => setNameInputFocused(true)}
            onBlur={() => setNameInputFocused(false)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setShowCreate(false); setNewName(''); } }}
          />
          <button
            onClick={handleCreate}
            disabled={isCreating || !newName.trim()}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '0.5rem 0.875rem', borderRadius: 8,
              background: '#10b981', color: '#fff',
              fontSize: '0.8125rem', fontWeight: 600, border: 'none',
              cursor: isCreating || !newName.trim() ? 'not-allowed' : 'pointer',
              opacity: isCreating || !newName.trim() ? 0.5 : 1,
              transition: 'background 0.18s', flexShrink: 0,
            }}
            onMouseEnter={e => { if (!isCreating && newName.trim()) e.currentTarget.style.background = '#059669'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#10b981'; }}
          >
            {isCreating ? <LoadingSpinner size="sm" /> : 'Create'}
          </button>
          <button
            onClick={() => { setShowCreate(false); setNewName(''); }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'transparent', border: '1px solid #1e2d45', cursor: 'pointer', color: '#475569', flexShrink: 0, transition: 'all 0.18s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.25)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569'; e.currentTarget.style.borderColor = '#1e2d45'; }}
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      )}

      {/* ── Watchlist panel ── */}
      {active && (
        <div style={{ background: '#0f1621', border: '1px solid #1a2235', borderRadius: 12, overflow: 'hidden' }}>
          {/* Panel header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.625rem 1rem',
            background: '#0c111d',
            borderBottom: '1px solid #1a2235',
            flexWrap: 'wrap', gap: '0.5rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '-0.01em' }}>
                {active.name}
              </span>
              <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#334155', background: '#1a2235', padding: '0.1rem 0.45rem', borderRadius: 6 }}>
                {active.stocks.length} stocks
              </span>
            </div>
            {active.quotes.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', fontWeight: 600, color: '#10b981', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', padding: '0.125rem 0.45rem', borderRadius: 99 }}>
                  ↑ {upCount}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', fontWeight: 600, color: '#ef4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', padding: '0.125rem 0.45rem', borderRadius: 99 }}>
                  ↓ {downCount}
                </span>
              </div>
            )}
          </div>
          <WatchlistTable
            stocks={active.stocks.map(s => {
              const ticker = typeof s === 'string' ? s : s.ticker;
              const name   = typeof s === 'string' ? undefined : s.name;
              const quote  = active.quotes.find(q => q.symbol === ticker);
              return { ticker, name, quote };
            })}
            onRemove={handleRemoveStock}
          />
        </div>
      )}
    </div>
  );
};
